import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useBranch } from '~/contexts/BranchContext';
import { getCategoryName, getCategoryDescription } from '~/utils/categoryHelper';
import { SharedMenuItemCard } from '~/components/shared';
import type { MenuItem } from '~/types/new_types';

interface SubCategory {
  id: string;
  items: MenuItem[];
  [key: string]: unknown;
}

interface SubCategoryAccordionProps {
  /** Array of subcategories to display */
  subcategories: SubCategory[];
  /** Parent category ID */
  categoryId: string;
  /** State object tracking which subcategories are expanded */
  expandedState: Record<string, boolean>;
  /** Callback to toggle subcategory expansion */
  toggleSubCategory: (id: string) => void;
  /** Callback when zoom button is clicked */
  onZoom: (item: MenuItem) => void;
  /** Callback when add button is clicked */
  onAdd?: (item: MenuItem) => void;
}

/**
 * Accordion component for displaying subcategories with expandable sections.
 * Each subcategory shows its items in a collapsible panel.
 */
function SubCategoryAccordionComponent({
  subcategories,
  categoryId,
  expandedState,
  toggleSubCategory,
  onZoom,
  onAdd,
}: SubCategoryAccordionProps) {
  const { theme } = useBranch();
  const { i18n } = useTranslation();

  if (subcategories.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="mt-8 space-y-6 border-t pt-6"
      style={{ borderColor: `${theme?.borderColor}20` }}
    >
      {subcategories.map((subCategory) => {
        const subCatId = `${categoryId}-${subCategory.id}`;
        const isExpanded = expandedState[subCatId] ?? true;

        return (
          <motion.div key={subCategory.id} className="overflow-hidden">
            {/* Subcategory Header */}
            <motion.button
              onClick={() => toggleSubCategory(subCatId)}
              className="w-full px-0 py-4 flex items-center justify-between transition-colors"
              style={{ backgroundColor: 'transparent' }}
              whileHover={{ backgroundColor: 'transparent' }}
            >
              <div className="flex items-center gap-3 flex-1">
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                  style={{ color: theme?.primaryColor, display: 'flex', alignItems: 'center' }}
                >
                  <FaChevronDown size={16} />
                </motion.div>

                <div className="text-left">
                  <h4
                    className="font-semibold text-sm md:text-base"
                    style={{ color: theme?.textColor }}
                  >
                    {getCategoryName(subCategory as any, i18n.language)}
                  </h4>
                  {getCategoryDescription(subCategory as any, i18n.language) && (
                    <p
                      className="text-xs line-clamp-1 mt-0.5"
                      style={{ color: `${theme?.textColor}60` }}
                    >
                      {getCategoryDescription(subCategory as any, i18n.language)}
                    </p>
                  )}
                </div>
              </div>

              {/* Item Count Badge */}
              <motion.div
                className="ml-2 px-3 py-1 rounded-full text-xs font-semibold"
                style={{
                  backgroundColor: `${theme?.primaryColor}20`,
                  color: theme?.primaryColor,
                }}
              >
                {subCategory.items.length}
              </motion.div>
            </motion.button>

            {/* Subcategory Items - Expandable */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 30 }}
                  className="overflow-hidden"
                >
                  <motion.div
                    className="space-y-4"
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    variants={{
                      visible: {
                        transition: {
                          staggerChildren: 0.02,
                          delayChildren: 0,
                        },
                      },
                    }}
                  >
                    {subCategory.items.map((item: MenuItem) => (
                      <SharedMenuItemCard
                        key={item.id}
                        item={item}
                        onZoom={onZoom}
                        onAdd={onAdd}
                      />
                    ))}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

SubCategoryAccordionComponent.displayName = 'SubCategoryAccordion';

export const SubCategoryAccordion = React.memo(SubCategoryAccordionComponent);
export default SubCategoryAccordion;
